import {
    Component,
    ElementRef,
    Input,
    Output,
    EventEmitter,
    OnInit,
    AfterViewInit,
    ViewChild,
    OnChanges,
    SimpleChanges,
    TemplateRef,
    OnDestroy,
} from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { Subscription } from "rxjs";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MaterialModule } from "../../material.module";
import { SpinnerComponent } from "../spinner/spinner.component";

export interface GridColumn {
    key: string;
    label: string;
    sortable?: boolean;
    filterable?: boolean;
    width?: string;
    type?: "text" | "number" | "date" | "custom" | "json-array";
    customTemplate?: TemplateRef<any>;
    accessor?: (item: any) => any; // For nested properties
    cellAlign?: string;
    tooltip?: string; // Tooltip shown on column header
    cellTooltip?: (item: any) => string; // Tooltip shown on cell hover
}

export interface GridAction {
    label: string;
    icon?: string;
    color?: string;
    tooltip?: string;
    visible?: (item: any) => boolean;
    disabled?: (item: any) => boolean;
}

export interface GridConfig {
    pageSize?: number;
    pageSizeOptions?: number[];
    showFirstLastButtons?: boolean;
    showColumnFilters?: boolean;
    enableSorting?: boolean;
    stickyHeader?: boolean;
    rowClass?: (row: any) => string;
}

export interface GridColumnGroup {
    label: string;
    keys: string[];
    borderLeft?: boolean;
    borderRight?: boolean;
    colBorderLeft?: boolean;
    colBorderRight?: boolean;
}

@Component({
    selector: "app-data-grid",
    templateUrl: "./data-grid.component.html",
    styleUrls: ["./data-grid.component.css"],
    imports: [MaterialModule, CommonModule, FormsModule, SpinnerComponent]
})
export class DataGridComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() data: any[] = [];
    @Input() columns: GridColumn[] = [];
    @Input() actions: GridAction[] = [];
    @Input() config: GridConfig = {};
    @Input() totalCount?: number;
    @Input() trackByFn?: (index: number, item: any) => any;
    @Input() columnGroups: GridColumnGroup[] = [];
    @Input() loading = false;
    @Input() initialPageIndex: number = 0;
    @Input() initialColumnFilters: { [key: string]: string } = {};
    @Input() filterOptions: { [column: string]: string[] } = {};
    @Input() filtersLoaded = false;

    @Output() actionClicked = new EventEmitter<{
        action: GridAction;
        item: any;
        event: Event;
    }>();
    @Output() columnFilterChanged = new EventEmitter<{
        column: string;
        value: string;
    }>();
    @Output() sortChanged = new EventEmitter<{
        column: string;
        direction: "asc" | "desc" | "";
    }>();
    @Output() pageChanged = new EventEmitter<{
        pageIndex: number;
        pageSize: number;
    }>();
    @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort!: MatSort;
    @ViewChild("tableScrollWrapper", { static: false }) tableScrollWrapper?: ElementRef<HTMLElement>;

    get skeletonRows() {
        return Array.from({ length: this.config?.pageSize || 10 }, (_, i) => i);
    }

    get skeletonColumns(): GridColumn[] {
        const columnMap = new Map(this.columns.map((column) => [column.key, column]));

        return this.displayedColumns.map((key) => {
            const column = columnMap.get(key);
            if (column) {
                return column;
            }

            if (key === "action" || key === "actions") {
                return {
                    key,
                    label: "Actions",
                    width: "230px",
                };
            }

            return {
                key,
                label: key,
            };
        });
    }

    get skeletonTopOffset(): string {
        const wrapper = this.tableScrollWrapper?.nativeElement;
        if (!wrapper) {
            let top = 56;
            if (this.groupHeaderCells?.length) top += 32;
            if (this.config?.showColumnFilters) top += 48;
            return top + 'px';
        }

        const selectors = [
            ".group-header-row",
            ".mat-mdc-header-row",
            ...(this.config?.showColumnFilters ? [".filter-row"] : []),
        ];

        const top = selectors
            .map((selector) => wrapper.querySelector(selector) as HTMLElement | null)
            .filter((element): element is HTMLElement => !!element)
            .reduce((sum, element) => sum + element.getBoundingClientRect().height, 0);

        return `${top}px`;
    }

    dataSource = new MatTableDataSource<any>([]);
    displayedColumns: string[] = [];
    filterColumns: string[] = [];
    columnFilters: { [key: string]: string } = {};
    columnFilterSelections: { [key: string]: string[] } = {};
    private columnFilterSelectionsSnapshot: { [key: string]: string[] } = {};
    currentPageIndex = 0;
    currentPageSize = 25;
    groupHeaderCells: Array<{
        label: string;
        flex: string;
        isGroup: boolean;
        borderLeft?: boolean;
        borderRight?: boolean;
        stickyLeft?: number;
        stickyRight?: boolean;
    }> = [];

    private subscriptions: Subscription[] = [];

    // Default configuration
    defaultConfig: GridConfig = {
        pageSize: 25,
        pageSizeOptions: [5, 10, 25, 50, 100],
        showFirstLastButtons: true,
        showColumnFilters: true,
        enableSorting: true,
        stickyHeader: true,
    };
    ngOnInit(): void {
        this.config = { ...this.defaultConfig, ...this.config };
        this.currentPageSize = this.config.pageSize || 25;
        this.currentPageIndex = this.initialPageIndex;
        if (this.initialColumnFilters && Object.keys(this.initialColumnFilters).length) {
            this.columnFilters = { ...this.initialColumnFilters };
        }
        this.setupColumns();
        this.setupDataSource();
        this.loadData();
        this.groupHeaderCells = this.computeGroupHeaderCells();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["data"] && !changes["data"].firstChange) {
            this.loadData();
        }
        if (changes["columns"] && !changes["columns"].firstChange) {
            this.setupColumns();
            this.groupHeaderCells = this.computeGroupHeaderCells();
        }
        if (changes["totalCount"] && !changes["totalCount"].firstChange) {
            if (this.paginator && this.totalCount !== undefined) {
                this.paginator.length = this.totalCount;
            }
        }
        if (changes["config"] && !changes["config"].firstChange) {
            this.config = { ...this.defaultConfig, ...this.config };
            this.setupColumns();
            this.setupDataSource();
        }
        if (changes["filterOptions"] && this.filterOptions) {
            // Ensure every filterable column has an initialized array for ngModel binding
            for (const key of Object.keys(this.filterOptions)) {
                if (!this.columnFilterSelections[key]) {
                    this.columnFilterSelections[key] = [];
                }
                // Restore multi-select state from existing text filters (e.g. state restored from navigation)
                if (this.columnFilters[key] && this.columnFilterSelections[key].length === 0) {
                    this.columnFilterSelections[key] = this.columnFilters[key].split('\u001F').filter(v => v.trim());
                }
            }
        }
        if (changes["filtersLoaded"] && this.filtersLoaded) {
            this.loadData();
        }
    }
    ngAfterViewInit(): void {
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.connectPaginatorAndSort();
        }, 0);
    }

    private setupColumns(): void {
        this.displayedColumns = this.columns.map((col) => col.key);
        // Only add actions column if it's not already in columns array
        const hasActionColumn = this.columns.some(col => col.key === 'action' || col.key === 'actions');
        if (this.actions && this.actions.length > 0 && !hasActionColumn) {
            this.displayedColumns.push("actions");
        }

        // Setup filter columns if column filtering is enabled
        if (this.config.showColumnFilters) {
            this.filterColumns = this.columns.map((col) => `${col.key}-filter`);
            if (this.actions && this.actions.length > 0 && !hasActionColumn) {
                this.filterColumns.push("actions-filter");
            }
        }
    }

    private setupDataSource(): void {
        this.setupColumnFilterPredicate();
        this.setupSortAccessor();
    }

    private setupColumnFilterPredicate(): void {
        this.dataSource.filterPredicate = (data: any, filter: string) => {
            // Always show the filter row
            if (data.isFilterRow) {
                return true;
            }

            // Check if any column filters are active
            const hasActiveFilters = Object.values(this.columnFilters).some(
                (filterValue) => filterValue && filterValue.trim()
            );

            if (!hasActiveFilters) {
                return true;
            }

            // Apply column-specific filters - ALL must pass
            for (const column in this.columnFilters) {
                const columnFilter = this.columnFilters[column];
                if (columnFilter && columnFilter.trim()) {
                    const columnConfig = this.columns.find((col) => col.key === column);
                    if (columnConfig) {
                        const fieldValue = String(
                            this.getColumnValue(data, columnConfig) ?? ""
                        ).toLowerCase();
                        if (!fieldValue.includes(columnFilter.toLowerCase())) {
                            return false;
                        }
                    }
                }
            }

            return true;
        };
    }

    getRowClass(row: any): string {
        if (row.isFilterRow) {
            return '';
        }
        return this.config.rowClass ? this.config.rowClass(row) : '';
    }

    getColumnClass(key: string): { [cls: string]: boolean } {
        const result: { [cls: string]: boolean } = {};
        if (!this.columnGroups?.length) return result;
        for (const group of this.columnGroups) {
            if (!group.keys.includes(key)) continue;
            result['group-col-start'] = group.keys[0] === key && group.colBorderLeft !== false;
            result['group-col-end'] = group.keys[group.keys.length - 1] === key && group.colBorderRight !== false;
            break;
        }
        return result;
    }

    hasActionColumn(): boolean {
        return this.columns.some(col => col.key === 'action' || col.key === 'actions');
    }

    computeGroupHeaderCells(): Array<{
        label: string;
        flex: string;
        isGroup: boolean;
        borderLeft?: boolean;
        borderRight?: boolean;
        stickyLeft?: number;
        stickyRight?: boolean;
    }> {
        if (!this.columnGroups?.length) return [];

        const result: Array<{
            label: string;
            flex: string;
            isGroup: boolean;
            borderLeft?: boolean;
            borderRight?: boolean;
            stickyLeft?: number;
            stickyRight?: boolean;
        }> = [];
        const processedKeys = new Set<string>();

        for (const col of this.columns) {
            if (processedKeys.has(col.key)) continue;

            const group = this.columnGroups.find(g => g.keys.includes(col.key));
            if (group) {
                const groupCols = this.columns.filter(c => group.keys.includes(c.key));
                const allHaveWidth = groupCols.every(c => c.width);
                const flex = allHaveWidth
                    ? `${groupCols.length} 0 ${groupCols.reduce((sum, c) => sum + parseInt(c.width!), 0)}px`
                    : `${groupCols.length}`;
                result.push({
                    label: group.label,
                    flex,
                    isGroup: true,
                    borderLeft: group.borderLeft,
                    borderRight: group.borderRight
                });
                group.keys.forEach(k => processedKeys.add(k));
            } else {
                // Check if this column should be sticky
                let stickyLeft: number | undefined;
                let stickyRight: boolean | undefined;

                if (col.key === 'appId') {
                    stickyLeft = 1;
                } else if (col.key === 'name') {
                    stickyLeft = 2;
                } else if (col.key === 'action' || col.key === 'actions') {
                    stickyRight = true;
                }

                result.push({
                    label: '',
                    flex: col.width ? `1 0 ${col.width}` : '1',
                    isGroup: false,
                    stickyLeft,
                    stickyRight
                });
                processedKeys.add(col.key);
            }
        }

        return result;
    }

    private setupSortAccessor(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            if (item.isFilterRow) return "";

            const column = this.columns.find((col) => col.key === property);
            if (column) {
                return this.getColumnValue(item, column);
            }
            return "";
        };

        // Custom sort to keep filter row at top
        this.dataSource.sortData = (data: any[], sort: MatSort) => {
            if (!sort.active || sort.direction === "") {
                return data;
            }

            const filterRow = data.find((item) => item.isFilterRow);
            const dataRows = data.filter((item) => !item.isFilterRow);

            const sortedDataRows = dataRows.sort((a, b) => {
                const isAsc = sort.direction === "asc";
                const valueA = this.dataSource.sortingDataAccessor(a, sort.active);
                const valueB = this.dataSource.sortingDataAccessor(b, sort.active);
                return this.compare(valueA, valueB, isAsc);
            });

            return filterRow ? [filterRow, ...sortedDataRows] : sortedDataRows;
        };
    }

    private loadData(): void {
        let processedData = [...this.data];

        // Add filter row if column filtering is enabled and filters are loaded
        if (this.config.showColumnFilters && this.filtersLoaded) {
            const filterRow = { isFilterRow: true } as any;
            processedData = [filterRow, ...processedData];
        }

        this.dataSource.data = processedData;
    }
    private connectPaginatorAndSort(): void {
        if (this.paginator) {
            if (this.totalCount !== undefined) {
                // Server-side pagination: set length manually, don't connect to dataSource
                this.paginator.length = this.totalCount;
                if (this.initialPageIndex > 0) {
                    this.paginator.pageIndex = this.initialPageIndex;
                }
            } else {
                // Client-side pagination: connect paginator to dataSource
                this.dataSource.paginator = this.paginator;

                // Override the paginator's connect method to always include filter row
                if (this.config.showColumnFilters) {
                    this.setupCustomPagination();
                }
            }
        }
        if (this.sort && this.config.enableSorting) {
            // Only connect sort to dataSource for client-side sorting
            if (this.totalCount === undefined) {
                this.dataSource.sort = this.sort;
            }
        }
    }

    private setupCustomPagination(): void {
        // Store original _filterData method
        const originalFilterData = this.dataSource._filterData.bind(
            this.dataSource
        );

        // Override _filterData to always include filter row at top
        this.dataSource._filterData = (data: any[]) => {
            const filteredData = originalFilterData(data);

            // Separate filter row from data rows
            const filterRow = filteredData.find((item) => item.isFilterRow);
            const dataRows = filteredData.filter((item) => !item.isFilterRow);

            // Return filter row + data rows
            return filterRow ? [filterRow, ...dataRows] : dataRows;
        };

        // Store original _pageData method
        const originalPageData = this.dataSource._pageData.bind(this.dataSource);

        // Override _pageData to always include filter row at top
        this.dataSource._pageData = (data: any[]) => {
            // Separate filter row from data rows
            const filterRow = data.find((item) => item.isFilterRow);
            const dataRows = data.filter((item) => !item.isFilterRow);

            // Apply pagination only to data rows
            const pagedDataRows = originalPageData(dataRows);

            // Return filter row + paged data rows
            return filterRow ? [filterRow, ...pagedDataRows] : pagedDataRows;
        };

        // Override _updatePaginator to correctly set the length for pagination
        const originalUpdatePaginator = this.dataSource._updatePaginator.bind(
            this.dataSource
        );
        this.dataSource._updatePaginator = (filteredDataLength: number) => {
            // Subtract 1 from length if filter row is present
            const hasFilterRow = this.dataSource.filteredData.some(
                (item) => item.isFilterRow
            );
            const adjustedLength = hasFilterRow
                ? filteredDataLength - 1
                : filteredDataLength;
            originalUpdatePaginator(adjustedLength);
        };
    }

    applyColumnFilter(column: string, value: string): void {
        const trimmedValue = value ? value.trim().toLowerCase() : "";

        if (trimmedValue) {
            this.columnFilters[column] = trimmedValue;
        } else {
            delete this.columnFilters[column];
        }

        // Emit filter change event for parent component to handle
        this.columnFilterChanged.emit({ column, value });

        // Only apply client-side filtering if not using server-side pagination
        if (this.totalCount === undefined) {
            // Setup the appropriate filter predicate
            this.setupColumnFilterPredicate();
            this.updateColumnFilters();
        }
    }
    private updateColumnFilters(): void {
        // Check if any column filters are active
        const hasActiveFilters = Object.values(this.columnFilters).some(
            (filterValue) => filterValue && filterValue.trim()
        );

        if (!hasActiveFilters) {
            // If no filters are active, reset to show all data
            this.dataSource.filter = " ";
            this.dataSource.filter = "";
        } else {
            // Trigger filter update
            this.dataSource.filter = "column-filter-update";
        }

        // Reset to first page when filters change
        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    onMultiSelectFilterOpened(columnKey: string): void {
        // Snapshot current selections so we can detect changes on close
        this.columnFilterSelectionsSnapshot[columnKey] = [...(this.columnFilterSelections[columnKey] || [])];
    }

    onMultiSelectFilterSelectionChange(columnKey: string, selectedValues: string[] | null | undefined): void {
        const current = selectedValues || [];
        this.columnFilterSelections[columnKey] = [...current];
        const joined = current.join('\u001F');
        this.applyColumnFilter(columnKey, joined);
    }

    onMultiSelectFilterClosed(columnKey: string): void {
        const current = this.columnFilterSelections[columnKey] || [];
        this.applyColumnFilter(columnKey, current.join('\u001F'));
    }

    getFilterTriggerLabel(columnKey: string): string {
        const selections = this.columnFilterSelections[columnKey] || [];
        if (selections.length === 0) return 'All';
        if (selections.length === 1) return selections[0];
        return `${selections.length} selected`;
    }

    getSortedOptions(columnKey: string): string[] {
        const options = this.filterOptions[columnKey] || [];
        const selected = new Set(this.columnFilterSelections[columnKey] || []);
        return [
            ...options.filter(o => selected.has(o)),
            ...options.filter(o => !selected.has(o))
        ];
    }

    onActionClick(action: GridAction, item: any, event: Event): void {
        event.stopPropagation();
        this.actionClicked.emit({ action, item, event });
    }

    onSortChange(sort: any): void {
        this.sortChanged.emit({
            column: sort.active,
            direction: sort.direction
        });
    }

    onPageChange(event: any): void {
        this.currentPageIndex = event.pageIndex;
        this.currentPageSize = event.pageSize;
        this.pageChanged.emit({
            pageIndex: event.pageIndex,
            pageSize: event.pageSize,
        });
    }

    get totalPages(): number {
        return Math.ceil(this.totalItems / (this.currentPageSize || 1)) || 1;
    }

    get pageSizeOptions(): number[] {
        return this.config.pageSizeOptions || [5, 10, 25, 50, 100];
    }

    get pageNumbers(): number[] {
        const total = this.totalPages;
        const current = this.currentPageIndex + 1;
        const delta = 2;
        const left = Math.max(1, current - delta);
        const right = Math.min(total, current + delta);
        const pages: number[] = [];
        for (let i = left; i <= right; i++) {
            pages.push(i);
        }
        return pages;
    }

    prevPage(): void {
        if (this.currentPageIndex > 0) {
            this.goToPage(this.currentPageIndex - 1);
        }
    }

    nextPage(): void {
        if (this.currentPageIndex < this.totalPages - 1) {
            this.goToPage(this.currentPageIndex + 1);
        }
    }

    goToPage(pageIndex: number): void {
        if (!this.paginator) { return; }
        this.paginator.pageIndex = pageIndex;
        this.paginator.page.emit({
            pageIndex: pageIndex,
            pageSize: this.paginator.pageSize,
            length: this.paginator.length,
        });
    }

    onPageSizeChange(event: Event): void {
        const newSize = Number((event.target as HTMLSelectElement).value);
        if (!this.paginator) { return; }
        this.paginator.pageIndex = 0;
        (this.paginator as any)._changePageSize(newSize);
    }

    getColumnValue(item: any, column: GridColumn): any {
        if (column.accessor) {
            return column.accessor(item);
        }

        // Handle nested properties (e.g., 'plant.name')
        const keys = column.key.split(".");
        let value = item;
        for (const key of keys) {
            value = value?.[key];
        }

        // Handle JSON array type
        if (column.type === 'json-array' && value) {
            return this.parseTechStack(value);
        }

        return value ?? "";
    }

    parseTechStack(json: string): string {
        if (!json) return '-';
        try {
            const arr = JSON.parse(json);
            if (Array.isArray(arr) && arr.length > 0) {
                return arr.join(', ');
            } else {
                return '-';
            }
        } catch {
            return '-';
        }
    }

    isActionVisible(action: GridAction, item: any): boolean {
        if (item.isFilterRow) return false;
        return action.visible ? action.visible(item) : true;
    }

    isActionDisabled(action: GridAction, item: any): boolean {
        return action.disabled ? action.disabled(item) : false;
    }

    get totalItems(): number {
        if (this.totalCount !== undefined) {
            return this.totalCount;
        }

        // Get the original data count excluding filter row
        const originalData = this.data || [];
        return originalData.length;
    }

    // Row predicate functions
    isFilterRow = (index: number, item: any) => item.isFilterRow;
    isDataRow = (index: number, item: any) => !item.isFilterRow;
    trackByItem(index: number, item: any): any {
        if (this.trackByFn) {
            return this.trackByFn(index, item);
        }
        return item.isFilterRow ? -1 : item.id || index;
    }

    getBootstrapButtonClass(color?: string): string {
        return `btn btn-${color}`;
    }

    private compare(
        a: string | number | null | undefined,
        b: string | number | null | undefined,
        isAsc: boolean
    ): number {
        const aMissing = a === null || a === undefined || a === '' || a === 'N/A';
        const bMissing = b === null || b === undefined || b === '' || b === 'N/A';
        if (aMissing || bMissing) {
            if (aMissing && bMissing) return 0;
            return aMissing ? 1 : -1;
        }

        const result = typeof a === 'number' && typeof b === 'number'
            ? a - b
            : a.toString().localeCompare(b.toString(), undefined, { numeric: true, sensitivity: 'base' });
        return result * (isAsc ? 1 : -1);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
